// Auto-sync: when the app is opened or the user navigates between screens,
// check when the last bank sync ran. If it was more than an hour ago,
// kick off a sync in the background.

import { api } from './api.js';

const SYNC_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const CHECK_DEBOUNCE_MS = 60 * 1000;      // don't re-check more than once per minute

interface Connection {
  id: number;
  status: string;
  last_synced_at: number | null;
}

let lastCheckAt = 0;
let syncInFlight = false;

async function checkAndSync(): Promise<void> {
  const now = Date.now();
  if (syncInFlight) return;
  if (now - lastCheckAt < CHECK_DEBOUNCE_MS) return;

  // Skip when unauthenticated — the connections call would redirect to login.
  const route = location.hash.slice(1).split('?')[0];
  if (!route || route === 'login') return;

  lastCheckAt = now;

  let connections: Connection[];
  try {
    const res = await api.get<{ connections: Connection[] }>('/connections');
    connections = res.connections;
  } catch {
    return; // unauthorized or network — silently skip
  }

  const needsSync = connections.some(c =>
    c.status === 'active' &&
    (c.last_synced_at == null || now - c.last_synced_at > SYNC_THRESHOLD_MS)
  );
  if (!needsSync) return;

  syncInFlight = true;
  try {
    await api.post('/connections/sync');
    // Home listens and refreshes its numbers in place.
    window.dispatchEvent(new CustomEvent('ch-synced'));
  } catch {
    // silent — this is a background task
  } finally {
    syncInFlight = false;
  }
}

/** Hook auto-sync into the app's navigation and visibility events. */
export function initAutoSync(): void {
  window.addEventListener('routechange', () => { void checkAndSync(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkAndSync();
  });
  // The initial `routechange` may fire before this listener is attached,
  // so trigger an explicit first check.
  void checkAndSync();
}
